import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, MessageCircle, Send } from "lucide-react";
import { SITE, waLink } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = ["Privatgarten", "Neubau / Außenanlage", "Bestandsgarten umgestalten", "Gewerbefläche", "Sonstiges"];
const SERVICES = [
  "Gartenplanung",
  "Landschaftsbau",
  "Terrassen",
  "Pflasterung & Wege",
  "Bepflanzung",
  "Gartenpflege",
];
const PERIODS = ["So schnell wie möglich", "In 1–3 Monaten", "In 3–6 Monaten", "Noch flexibel"];

const EMPTY = {
  vorname: "",
  nachname: "",
  email: "",
  telefon: "",
  ort: "",
  projektart: PROJECT_TYPES[0],
  leistung: SERVICES[0],
  zeitraum: PERIODS[0],
  nachricht: "",
};

const field =
  "w-full rounded-2xl border border-[#17251C]/15 bg-[#F3EDE2] px-5 py-4 text-sm text-[#1D231D] outline-none transition-colors duration-300 placeholder:text-[#9AA396] focus:border-[#B4623C]";
const labelCls = "mb-2 block text-[0.6rem] font-bold uppercase tracking-[0.24em] text-[#6E7A69]";

export const QuoteForm = () => {
  const [data, setData] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const summary = () =>
    `Hallo Hanse Garten Bremen, ich möchte gerne mein Gartenprojekt besprechen.\n\n` +
    `Name: ${data.vorname} ${data.nachname}\nE-Mail: ${data.email}\nTelefon: ${data.telefon}\nOrt: ${data.ort}\n` +
    `Art des Projekts: ${data.projektart}\nGewünschte Leistung: ${data.leistung}\nWunschzeitraum: ${data.zeitraum}\n` +
    `Nachricht: ${data.nachricht}`;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setState("loading");
    try {
      await axios.post(`${API}/anfragen`, data);
      setState("success");
      window.open(waLink(summary()), "_blank", "noopener");
    } catch (err) {
      setState("idle");
      setError("Die Anfrage konnte nicht gesendet werden. Schreiben Sie uns gerne direkt über WhatsApp.");
    }
  };

  if (state === "success") {
    return (
      <motion.div
        data-testid="quote-form-success"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-[2.5rem] border border-[#17251C]/12 bg-[#DCCDB4]/45 p-10 md:p-16"
      >
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#2F3D2E] text-[#F3EDE2]">
          <Check size={28} />
        </span>
        <h3 className="display-xl mt-8 text-3xl text-[#17251C] md:text-[3rem]">Danke für Ihre Anfrage.</h3>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-[#6E7A69]">
          Ihre Angaben sind bei uns eingegangen. Wir melden uns persönlich, um Ihr Gartenprojekt zu besprechen. Sie
          erreichen uns jederzeit direkt per WhatsApp unter {SITE.whatsappNumber} oder per E-Mail an {SITE.email}.
        </p>
        <div className="mt-9 flex flex-wrap gap-3">
          <a
            href={waLink(summary())}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            data-testid="success-whatsapp-btn"
          >
            <MessageCircle size={15} /> Per WhatsApp nachfassen
          </a>
          <button
            type="button"
            onClick={() => {
              setData(EMPTY);
              setState("idle");
            }}
            className="btn-outline"
            data-testid="quote-form-reset-btn"
          >
            Neue Anfrage
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="quote-form"
      className="rounded-[2.5rem] border border-[#17251C]/12 bg-[#DCCDB4]/35 p-6 md:p-12"
    >
      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="vorname">Vorname *</label>
          <input id="vorname" required value={data.vorname} onChange={set("vorname")} className={field} placeholder="Max" data-testid="quote-input-vorname" />
        </div>
        <div>
          <label className={labelCls} htmlFor="nachname">Nachname *</label>
          <input id="nachname" required value={data.nachname} onChange={set("nachname")} className={field} placeholder="Mustermann" data-testid="quote-input-nachname" />
        </div>
        <div>
          <label className={labelCls} htmlFor="email">E-Mail *</label>
          <input id="email" type="email" required value={data.email} onChange={set("email")} className={field} placeholder="max@beispiel.de" data-testid="quote-input-email" />
        </div>
        <div>
          <label className={labelCls} htmlFor="telefon">Telefon</label>
          <input id="telefon" value={data.telefon} onChange={set("telefon")} className={field} placeholder="0176 …" data-testid="quote-input-telefon" />
        </div>
        <div>
          <label className={labelCls} htmlFor="ort">Ort *</label>
          <input id="ort" required value={data.ort} onChange={set("ort")} className={field} placeholder="Bremen" data-testid="quote-input-ort" />
        </div>
        <div>
          <label className={labelCls} htmlFor="projektart">Art des Projekts</label>
          <select id="projektart" value={data.projektart} onChange={set("projektart")} className={field} data-testid="quote-select-projektart">
            {PROJECT_TYPES.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="leistung">Gewünschte Leistung</label>
          <select id="leistung" value={data.leistung} onChange={set("leistung")} className={field} data-testid="quote-select-leistung">
            {SERVICES.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="zeitraum">Wunschzeitraum</label>
          <select id="zeitraum" value={data.zeitraum} onChange={set("zeitraum")} className={field} data-testid="quote-select-zeitraum">
            {PERIODS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls} htmlFor="nachricht">Nachricht</label>
          <textarea id="nachricht" rows={5} value={data.nachricht} onChange={set("nachricht")} className={field} placeholder="Beschreiben Sie kurz Ihr Gartenprojekt …" data-testid="quote-input-nachricht" />
        </div>
      </div>

      <p className="mt-5 rounded-2xl border border-dashed border-[#B4623C]/40 bg-[#F3EDE2]/70 px-5 py-4 text-xs leading-relaxed text-[#6E7A69]">
        Fotos Ihres Gartens (optional): Senden Sie uns Ihre Bilder am einfachsten direkt per WhatsApp oder per E-Mail an{" "}
        {SITE.email} – so können wir Ihr Projekt schon vorab einschätzen.
      </p>

      {error && <p className="mt-4 text-sm font-medium text-[#B4623C]" data-testid="quote-form-error">{error}</p>}

      <div className="mt-7 flex flex-wrap gap-3">
        <button type="submit" disabled={state === "loading"} className="btn-primary" data-testid="quote-form-submit-btn">
          {state === "loading" ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />}
          Anfrage senden
        </button>
        <a href={waLink(summary())} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="quote-form-whatsapp-btn">
          <MessageCircle size={15} /> Per WhatsApp anfragen
        </a>
      </div>
      <p className="mt-5 text-xs leading-relaxed text-[#8B948A]">
        Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Angaben zur Bearbeitung der Anfrage zu. Details in der
        Datenschutzerklärung.
      </p>
    </form>
  );
};
